import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Bot, Sparkles, TrendingUp, MessageSquare, Image, Video, BarChart3, CheckCircle2, ArrowRight, Play, Pause } from 'lucide-react';

const DemoPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const demoSteps = [
    {
      title: "Step 1: Connect Your Accounts",
      description: "Link your website, Google My Business, and social media accounts in just 60 seconds",
      icon: Bot,
      color: "from-blue-500 to-cyan-500",
      animation: "Connect GMB, Instagram, Facebook, LinkedIn, YouTube, X...",
      demo: {
        type: "connection",
        platforms: ["Google My Business", "Instagram", "Facebook", "LinkedIn", "YouTube", "X (Twitter)"],
        status: "All Connected ✓"
      }
    },
    {
      title: "Step 2: AI Analyzes Your Business",
      description: "AI studies your industry, competitors, target audience, and keywords",
      icon: Sparkles,
      color: "from-purple-500 to-pink-500",
      animation: "Analyzing 250+ data points...",
      demo: {
        type: "analysis",
        insights: [
          "✓ Industry: Healthcare/Wellness",
          "✓ Top 15 Keywords Identified",
          "✓ 8 Competitors Analyzed",
          "✓ Target Audience Profiled",
          "✓ Content Strategy Generated"
        ]
      }
    },
    {
      title: "Step 3: AI Generates SEO Content",
      description: "Weekly SEO audits, keyword tracking, and optimizations",
      icon: TrendingUp,
      color: "from-green-500 to-emerald-500",
      animation: "Running SEO audit...",
      demo: {
        type: "seo",
        metrics: [
          { label: "Site Health Score", value: "92/100", change: "+8" },
          { label: "Keywords Ranked", value: "47", change: "+12" },
          { label: "Top 3 Rankings", value: "8", change: "+3" },
          { label: "Backlinks", value: "156", change: "+24" }
        ]
      }
    },
    {
      title: "Step 4: AI Creates Social Posts",
      description: "5-10 engaging posts per week across all platforms",
      icon: MessageSquare,
      color: "from-orange-500 to-red-500",
      animation: "Generating social posts...",
      demo: {
        type: "social",
        posts: [
          { platform: "Instagram", content: "🌟 5 Ways to Boost Your Immunity This Winter...", status: "Scheduled" },
          { platform: "Facebook", content: "Did you know? 70% of people don't...", status: "Posted" },
          { platform: "LinkedIn", content: "Healthcare innovation: How AI is transforming...", status: "Scheduled" }
        ]
      }
    },
    {
      title: "Step 5: AI Generates Images",
      description: "2 professional marketing images per week using DALL-E 3",
      icon: Image,
      color: "from-violet-500 to-purple-500",
      animation: "Creating AI images...",
      demo: {
        type: "images",
        samples: [
          { title: "Wellness Tips", style: "Modern gradient", status: "Generated" },
          { title: "Product Showcase", style: "Professional photo", status: "Generated" }
        ]
      }
    },
    {
      title: "Step 6: AI Creates Videos",
      description: "2 engaging video reels per week with RunwayML Gen-3",
      icon: Video,
      color: "from-yellow-500 to-orange-500",
      animation: "Producing AI videos...",
      demo: {
        type: "videos",
        videos: [
          { title: "30-sec Product Demo", duration: "0:30", status: "Rendered" },
          { title: "Customer Testimonial", duration: "0:45", status: "Rendered" }
        ]
      }
    },
    {
      title: "Step 7: Auto-Posting Everywhere",
      description: "AI publishes content at optimal times across all platforms",
      icon: CheckCircle2,
      color: "from-green-500 to-cyan-500",
      animation: "Publishing content...",
      demo: {
        type: "posting",
        schedule: [
          { day: "Monday", posts: 2, status: "Posted ✓" },
          { day: "Wednesday", posts: 2, status: "Scheduled" },
          { day: "Friday", posts: 1, status: "Scheduled" }
        ]
      }
    },
    {
      title: "Step 8: Weekly Reports",
      description: "Comprehensive performance reports delivered via email & WhatsApp",
      icon: BarChart3,
      color: "from-blue-500 to-purple-500",
      animation: "Generating insights...",
      demo: {
        type: "reports",
        metrics: [
          { label: "Total Reach", value: "45,230", change: "+23%" },
          { label: "Engagement Rate", value: "8.4%", change: "+2.1%" },
          { label: "New Followers", value: "892", change: "+156" },
          { label: "Website Visits", value: "3,247", change: "+34%" }
        ]
      }
    }
  ];

  useEffect(() => {
    if (isPlaying) {
      const timer = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            if (currentStep < demoSteps.length - 1) {
              setCurrentStep(prev => prev + 1);
              return 0;
            } else {
              setIsPlaying(false);
              return 100;
            }
          }
          return prev + 2;
        });
      }, 100);
      return () => clearInterval(timer);
    }
  }, [isPlaying, currentStep, demoSteps.length]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleStepClick = (index) => {
    setCurrentStep(index);
    setProgress(0);
    setIsPlaying(false);
  };

  const currentStepData = demoSteps[currentStep];
  const Icon = currentStepData.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Navigation */}
      <nav className="navbar px-6 py-4 bg-white/80 backdrop-blur-xl border-b">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-11 h-11 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
              A
            </div>
            <span className="text-2xl font-bold heading bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AutomateSEO.ai</span>
          </div>
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/')}>Back to Home</Button>
            <Button onClick={() => navigate('/register')} className="bg-gradient-to-r from-blue-600 to-purple-600">
              Start Free Trial
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="bg-green-100 border-l-4 border-green-500 text-green-800 p-4 mb-6 rounded-lg max-w-2xl mx-auto">
            <p className="font-semibold mb-1">🎉 Start Your 3-Day Free Trial</p>
            <p className="text-sm">No credit card required • Full feature access • Cancel anytime</p>
            <p className="text-xs mt-2 text-green-700">Not satisfied? Get a full refund within 3 days of payment</p>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold heading mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Interactive Demo</span>
          </h1>
          <p className="text-xl text-gray-600 mb-6">See how AI automates your entire digital marketing</p>
          <Button 
            size="lg" 
            onClick={handlePlayPause}
            className="bg-gradient-to-r from-blue-600 to-purple-600"
          >
            {isPlaying ? (
              <>
                <Pause className="mr-2 h-5 w-5" />
                Pause Demo
              </>
            ) : (
              <>
                <Play className="mr-2 h-5 w-5" />
                Play Demo
              </>
            )}
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {/* Steps Sidebar */}
          <div className="lg:col-span-1">
            <Card className="card sticky top-24">
              <CardHeader>
                <CardTitle>Demo Steps</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {demoSteps.map((step, index) => {
                  const StepIcon = step.icon;
                  return (
                    <div
                      key={index}
                      onClick={() => handleStepClick(index)}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        currentStep === index
                          ? 'bg-gradient-to-r ' + step.color + ' text-white shadow-lg'
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <StepIcon className="w-5 h-5" />
                        <div className="text-sm font-medium">
                          Step {index + 1}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Demo Content */}
          <div className="lg:col-span-2">
            <Card className="card">
              <CardHeader>
                <div className={`w-16 h-16 bg-gradient-to-br ${currentStepData.color} rounded-2xl flex items-center justify-center mb-4`}>
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-3xl">{currentStepData.title}</CardTitle>
                <p className="text-gray-600 text-lg mt-2">{currentStepData.description}</p>
              </CardHeader>
              <CardContent>
                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full bg-gradient-to-r ${currentStepData.color} transition-all`}
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  {isPlaying && (
                    <p className="text-sm text-gray-500 mt-2 animate-pulse">{currentStepData.animation}</p>
                  )}
                </div>

                {/* Demo Content */}
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border-2 border-gray-100">
                  {currentStepData.demo.type === 'connection' && (
                    <div className="space-y-3">
                      {currentStepData.demo.platforms.map((platform, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <span className="font-medium">{platform}</span>
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        </div>
                      ))}
                      <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-800 font-medium">✓ {currentStepData.demo.status}</p>
                      </div>
                    </div>
                  )}

                  {currentStepData.demo.type === 'analysis' && (
                    <div className="space-y-2">
                      {currentStepData.demo.insights.map((insight, i) => (
                        <div key={i} className="p-3 bg-white rounded-lg border">
                          {insight}
                        </div>
                      ))}
                    </div>
                  )}

                  {currentStepData.demo.type === 'seo' && (
                    <div className="grid grid-cols-2 gap-4">
                      {currentStepData.demo.metrics.map((metric, i) => (
                        <div key={i} className="p-4 bg-white rounded-lg border">
                          <p className="text-sm text-gray-500">{metric.label}</p>
                          <p className="text-3xl font-bold mt-2">{metric.value}</p>
                          <p className="text-sm text-green-600 mt-1">{metric.change}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {currentStepData.demo.type === 'social' && (
                    <div className="space-y-3">
                      {currentStepData.demo.posts.map((post, i) => (
                        <div key={i} className="p-4 bg-white rounded-lg border">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-blue-600">{post.platform}</span>
                            <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">{post.status}</span>
                          </div>
                          <p className="text-sm text-gray-600">{post.content}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {currentStepData.demo.type === 'images' && (
                    <div className="space-y-3">
                      {currentStepData.demo.samples.map((img, i) => (
                        <div key={i} className="p-4 bg-white rounded-lg border">
                          <div className="flex items-center gap-4">
                            <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-pink-400 rounded-lg"></div>
                            <div>
                              <p className="font-medium">{img.title}</p>
                              <p className="text-sm text-gray-500">Style: {img.style}</p>
                              <p className="text-xs text-green-600 mt-1">✓ {img.status}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {currentStepData.demo.type === 'videos' && (
                    <div className="space-y-3">
                      {currentStepData.demo.videos.map((video, i) => (
                        <div key={i} className="p-4 bg-white rounded-lg border">
                          <div className="flex items-center gap-4">
                            <div className="w-32 h-20 bg-gradient-to-br from-orange-400 to-red-400 rounded-lg flex items-center justify-center">
                              <Play className="w-8 h-8 text-white" />
                            </div>
                            <div>
                              <p className="font-medium">{video.title}</p>
                              <p className="text-sm text-gray-500">Duration: {video.duration}</p>
                              <p className="text-xs text-green-600 mt-1">✓ {video.status}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {currentStepData.demo.type === 'posting' && (
                    <div className="space-y-3">
                      {currentStepData.demo.schedule.map((item, i) => (
                        <div key={i} className="p-4 bg-white rounded-lg border">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{item.day}</p>
                              <p className="text-sm text-gray-500">{item.posts} posts</p>
                            </div>
                            <span className="text-sm font-medium text-green-600">{item.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {currentStepData.demo.type === 'reports' && (
                    <div className="grid grid-cols-2 gap-4">
                      {currentStepData.demo.metrics.map((metric, i) => (
                        <div key={i} className="p-4 bg-white rounded-lg border">
                          <p className="text-sm text-gray-500">{metric.label}</p>
                          <p className="text-3xl font-bold mt-2">{metric.value}</p>
                          <p className="text-sm text-green-600 mt-1">↑ {metric.change}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between mt-8">
                  <Button
                    variant="outline"
                    onClick={() => currentStep > 0 && handleStepClick(currentStep - 1)}
                    disabled={currentStep === 0}
                  >
                    Previous
                  </Button>
                  {currentStep < demoSteps.length - 1 ? (
                    <Button
                      onClick={() => handleStepClick(currentStep + 1)}
                      className="bg-gradient-to-r from-blue-600 to-purple-600"
                    >
                      Next Step
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      onClick={() => navigate('/register')}
                      className="bg-gradient-to-r from-blue-600 to-purple-600"
                    >
                      Start Your Free Trial
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoPage;
